import React, {useContext, useEffect, useState} from "react";
import {ManualClearingService} from "./ManualClearingService";
import SnackbarContext from "../../providers/snackbar-context";
import {Status} from "../../graphql/audit-overview-api/api";
import ManualClearingLoadingModal from "./ManualClearingLoadingModal";
import ManualClearingNoInventoryModal from "./ManualClearingNoInventoryModal";
import ManualClearingInventoryFoundModal from "./ManualClearingInventoryFoundModal";
import ManualClearingSuccessModal from "./ManualClearingSuccessModal";
import "./ManualClearingConfirmModal.css";

type ModalView = "loading" | "no-inventory" | "inventory-found" | "success";

interface DocumentBondedQuantity {
    documentId: string;
    totalBondedQuantity: number | null;
}

interface ManualClearingConfirmModalProps {
    isOpen: boolean;
    onClose: (isConfirmed: boolean) => void;
    documentIds: string[];
    reason: string;
    comment: string;
    manualClearingService: ManualClearingService;
}

function ManualClearingConfirmModal({
                                        isOpen,
                                        onClose,
                                        documentIds,
                                        reason,
                                        comment,
                                        manualClearingService,
                                    }: ManualClearingConfirmModalProps) {
    const snackbarCtx = useContext(SnackbarContext);
    const [view, setView] = useState<ModalView>("loading");
    const [processing, setProcessing] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [eligibleDocumentIds, setEligibleDocumentIds] = useState<string[]>([]);
    const [noInventoryDocumentIds, setNoInventoryDocumentIds] = useState<string[]>([]);
    const [totalBondedUnits, setTotalBondedUnits] = useState<number>(0);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (documentIds.length === 0) {
            setEligibleDocumentIds([]);
            setNoInventoryDocumentIds([]);
            setTotalBondedUnits(0);
            setView("no-inventory");
            return;
        }

        setView("loading");
        manualClearingService.getBondedQuantitiesByDocumentIds(documentIds)
            .then(result => {
                const response: DocumentBondedQuantity[] = result.data?.getBondedQuantitiesByDocumentIds ?? [];
                const quantitiesById = new Map<string, number>();
                response.forEach(entry => {
                    quantitiesById.set(entry.documentId, entry.totalBondedQuantity ?? 0);
                });

                const eligible: string[] = [];
                const noInventory: string[] = [];
                let total = 0;
                documentIds.forEach(id => {
                    const quantity = quantitiesById.get(id) ?? 0;
                    if (quantity > 0) {
                        eligible.push(id);
                        total += quantity;
                    } else {
                        noInventory.push(id);
                    }
                });

                setEligibleDocumentIds(eligible);
                setNoInventoryDocumentIds(noInventory);
                setTotalBondedUnits(total);
                setView(eligible.length === 0 ? "no-inventory" : "inventory-found");
            })
            .catch(error => {
                snackbarCtx.displayMsg("Error loading bonded quantities: " + error, "error");
                setView("no-inventory");
            });
    }, [isOpen, manualClearingService, documentIds, snackbarCtx]);

    const handleSubmit = () => {
        setProcessing(true);

        manualClearingService.submitManualClearing(eligibleDocumentIds, reason, comment)
            .then(response => {
                setProcessing(false);
                const result = response.data?.submitManualClearing;
                if (result?.status === Status.SUCCESS) {
                    setConfirmed(true);
                    setView("success");
                } else {
                    const message = result?.error?.message ?? "Unknown error";
                    snackbarCtx.displayMsg("Failed to submit event: " + message, "error");
                }
            })
            .catch(error => {
                setProcessing(false);
                snackbarCtx.displayMsg("Failed to submit event: " + error, "error");
            });
    };

    const handleDismiss = () => onClose(confirmed);

    if (!isOpen) {
        return null;
    }

    if (view === "loading") {
        return (
            <ManualClearingLoadingModal
                isOpen={true}
                onDismiss={handleDismiss}
            />
        );
    }

    if (view === "no-inventory") {
        return (
            <ManualClearingNoInventoryModal
                isOpen={true}
                onDismiss={handleDismiss}
                documentIds={documentIds}
            />
        );
    }

    if (view === "success") {
        return (
            <ManualClearingSuccessModal
                isOpen={true}
                onDismiss={handleDismiss}
            />
        );
    }

    return (
        <ManualClearingInventoryFoundModal
            isOpen={true}
            onDismiss={handleDismiss}
            onConfirm={handleSubmit}
            eligibleDocumentIds={eligibleDocumentIds}
            noInventoryDocumentIds={noInventoryDocumentIds}
            totalBondedUnits={totalBondedUnits}
            reason={reason}
            comment={comment}
            processing={processing}
        />
    );
}

export default ManualClearingConfirmModal;
